<template>
  <modal
    title="Move Selected Cards"
    :open="open"
    @close="closeModal"
  >
    <form @submit.stop.prevent="submit">
      <p class="description">
        Move {{ selectedCount }} selected {{ selectedCount === 1 ? 'card' : 'cards' }} to another collection.
      </p>

      <select-input
        v-model="targetCollectionId"
        label="Destination Collection"
        :options="collectionOptions"
      />

      <div class="button-row">
        <btn
          type="submit"
          :loading="submitting"
          :disabled="disabled"
          loading-text="Moving"
        >
          Move
        </btn>

        <btn kind="secondary" @click="closeModal">
          Cancel
        </btn>
      </div>
    </form>
  </modal>
</template>

<script>
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';

import { authService } from '@/services';

import { COLLECTION_SIZE_LIMIT } from '@cardladder/shared/constants';

export default {
  props: {
    open: Boolean,
  },

  data() {
    return {
      targetCollectionId: null,
      submitting: false,
    };
  },

  watch: {
    open(value) {
      if (value) {
        this.targetCollectionId = this.collectionOptions[0]?.value || null;
      }
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      collection: (state) => state.collection,
      collections: (state) => state.collections,
      collectionSelectedIds: (state) => state.collectionSelectedIds,
    }),

    selectedCount() {
      return this.collectionSelectedIds?.length || 0;
    },

    collectionOptions() {
      if (!this.collections || !this.collections.length) {
        return [];
      }

      return this.collections
        .filter(({ id }) => id !== this.collection.id)
        .map(({ id, name }) => ({
          value: id,
          label: name || 'Collection',
        }))
        .sort((a, b) => a.label?.localeCompare(b.label));
    },

    disabled() {
      return !this.targetCollectionId || !this.selectedCount || this.submitting;
    },
  },

  methods: {
    async submit() {
      if (this.disabled) {
        return;
      }

      const targetCollection = this.collections.find(({ id }) => id === this.targetCollectionId);

      if (!targetCollection) {
        this.$toasted.error('Please select a collection');
        return;
      }

      const destinationCount = (targetCollection.numCards || 0) + this.selectedCount;

      if (destinationCount > COLLECTION_SIZE_LIMIT) {
        this.$toasted.error(`That collection can only hold ${COLLECTION_SIZE_LIMIT} cards`);
        return;
      }

      this.submitting = true;

      try {
        await Promise.all(this.collectionSelectedIds.map((id) => authService.moveCollectionCard(
          this.user.uid,
          this.collection.id,
          this.targetCollectionId,
          id,
        )));

        this.$store.commit('SET_COLLECTION_SELECTED_IDS', []);
        this.$toasted.success('Cards are being moved to the other collection!');
        this.closeModal();

        setTimeout(() => {
          this.$bus.$emit('collection-changed');
        }, 4000);
      } catch (e) {
        this.$toasted.error('Error moving these cards');
        Sentry.captureException(e);
      }

      this.submitting = false;
    },

    closeModal() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.description {
  margin: 0 0 $gp;
}
</style>
